import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addSiteTitle, editSiteTitle } from '../../../../api/endpoints/cmsSite';
import type { SiteTitle } from '../../../../api/types/cmsSite';
import { FormDialog } from '../../../../components/common';

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  subTitle: z.string().optional(),
  position: z.coerce.number().int().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { title: '', subTitle: '', position: 0 };

function toFormValues(siteTitle: SiteTitle): FormValues {
  return {
    title: siteTitle.title ?? '',
    subTitle: siteTitle.subTitle ?? '',
    position: siteTitle.position ?? 0,
  };
}

/** Create/edit dialog for Site Başlıkları. Confirmed via local-backend swagger + live curl
 *  (see api/types/cmsSite.ts header). Always rendered inside a specific site's tab, so
 *  `site_Id` is injected from the `siteId` prop rather than user-editable. */
export function SiteTitleFormDialog({
  open,
  siteTitle,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  siteTitle: SiteTitle | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!siteTitle;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(siteTitle ? toFormValues(siteTitle) : emptyValues);
  }, [open, siteTitle, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && siteTitle
        ? editSiteTitle({ id: siteTitle.id, site_Id: siteId, ...values })
        : addSiteTitle({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Site başlığı güncellendi.' : 'Site başlığı oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'siteTitles'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Site Başlığını Güncelle' : 'Yeni Site Başlığı'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
      <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Alt Başlık" {...register('subTitle')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Sıra" {...register('position')} />
            </Grid>
      </Grid>
    </FormDialog>
  );
}
